import { Link } from "react-router-dom";

export default function Testimonials() {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero */}
      <div className="bg-gradient-to-r from-blue-600 to-sky-700 text-white">
        <div className="container mx-auto px-4 py-16 text-center">
          <div className="inline-block bg-blue-500 text-blue-100 text-xs font-semibold px-3 py-1 rounded-full mb-4 uppercase tracking-wide">
            Private Beta · Qatar First
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold mb-4">🏫 Pilot ExamLock at Your School</h1>
          <p className="text-lg sm:text-xl text-blue-100 max-w-2xl mx-auto mb-8">
            We are onboarding a limited number of Qatar and GCC schools for our private beta.
            Pilots are free to set up and typically run for a full term or semester.
          </p>
          <Link
            to="/contact"
            className="inline-block bg-white text-blue-700 px-8 py-4 rounded-lg font-bold text-lg hover:bg-blue-50 transition duration-200 shadow-lg"
          >
            📩 Request a School Pilot
          </Link>
        </div>
      </div>

      {/* What a Pilot Includes */}
      <div className="py-16 bg-white">
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-10">What a Pilot Includes</h2>
          <div className="grid sm:grid-cols-2 gap-6">
            <div className="bg-blue-50 rounded-xl p-6 border border-blue-100">
              <div className="text-3xl mb-3">⚡</div>
              <h3 className="font-bold text-gray-900 mb-2">1–2 Day Setup</h3>
              <p className="text-gray-600 text-sm">
                Your teachers can be creating and assigning exams within one to two business days of signup.
                No IT deployment needed — runs entirely in the browser.
              </p>
            </div>
            <div className="bg-blue-50 rounded-xl p-6 border border-blue-100">
              <div className="text-3xl mb-3">🤖</div>
              <h3 className="font-bold text-gray-900 mb-2">AI Exam Builder</h3>
              <p className="text-gray-600 text-sm">
                Teachers enter a topic or paste a learning objective and the AI generates a full set of questions.
                Works with IB, IGCSE, NGSS, and custom curricula.
              </p>
            </div>
            <div className="bg-blue-50 rounded-xl p-6 border border-blue-100">
              <div className="text-3xl mb-3">🔐</div>
              <h3 className="font-bold text-gray-900 mb-2">Real-Time Monitoring</h3>
              <p className="text-gray-600 text-sm">
                Teachers watch all students live during an exam. Suspicious events are flagged and logged with timestamps
                — providing evidence for conversations with students and parents.
              </p>
            </div>
            <div className="bg-blue-50 rounded-xl p-6 border border-blue-100">
              <div className="text-3xl mb-3">📊</div>
              <h3 className="font-bold text-gray-900 mb-2">Auto-Grading & Reports</h3>
              <p className="text-gray-600 text-sm">
                Multiple choice graded instantly. Short answers marked by AI. Teachers receive a detailed
                results report without any manual marking.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Who We Are Looking For */}
      <div className="py-16 bg-gray-50">
        <div className="container mx-auto px-4 max-w-3xl text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Who We Are Looking For</h2>
          <p className="text-gray-600 mb-8">
            We want to work closely with a small number of schools to refine ExamLock for the Qatar market.
            If you are a department head, exam officer, coordinator, or teacher leader at any of the following,
            we would love to hear from you:
          </p>
          <div className="flex flex-wrap justify-center gap-3 mb-10">
            {[
              "Qatar Foundation Schools",
              "Private International Schools",
              "IGCSE / IB Centres",
              "NGSS Science Departments",
              "Government Schools (MoE)",
              "Higher Education Institutions",
            ].map((label) => (
              <span key={label} className="bg-white border border-blue-200 text-blue-800 text-sm px-4 py-2 rounded-full font-medium">
                {label}
              </span>
            ))}
          </div>
          <Link
            to="/contact"
            className="inline-block bg-blue-600 text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-blue-700 transition duration-200 shadow-lg"
          >
            📩 Get in Touch
          </Link>
          <p className="text-gray-400 text-sm mt-4">
            Features and limits may change during beta. We will keep pilot schools informed of any changes.
          </p>
        </div>
      </div>

      {/* Early Access Feedback */}
      <div className="py-16 bg-white">
        <div className="container mx-auto px-4 max-w-3xl">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-4">💬 Early Access Feedback</h2>
          <p className="text-gray-500 text-center mb-8 text-sm">Quotes from beta teachers during the current private beta period.</p>
          <div className="space-y-6">
            {[
              {
                quote: "The AI exam builder saves a huge amount of prep time. Real-time monitoring during tests is genuinely useful.",
                role: "Secondary School, Doha",
              },
              {
                quote: "The violation log is detailed and timestamped. Much easier to have honest conversations with students about academic integrity.",
                role: "High School, Qatar",
              },
              {
                quote: "Creating a full quiz from scratch used to take me an hour. With ExamLock it takes about ten minutes.",
                role: "Middle School, UAE",
              },
            ].map((item, i) => (
              <div key={i} className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                <p className="text-gray-700 italic mb-3">"{item.quote}"</p>
                <p className="text-gray-500 text-sm font-medium">{item.role} — early access beta teacher</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* CTA */}
      <div className="py-16 bg-gradient-to-r from-blue-600 to-sky-700 text-white text-center">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold mb-4">Ready to Run a Pilot?</h2>
          <p className="text-blue-100 mb-8 max-w-xl mx-auto">
            Contact us and we will set up a call to understand your school's needs and get you started.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="inline-block bg-white text-blue-700 px-8 py-3 rounded-lg font-bold hover:bg-blue-50 transition duration-200 shadow-lg"
            >
              📩 Request Pilot
            </Link>
            <Link
              to="/teacher-register"
              className="inline-block border border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-blue-700 transition duration-200"
            >
              Start Free Trial (Beta)
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
